import React, { useEffect, useState, useRef } from 'react';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import documentExportService from '../../services/documentExport.service';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import { processContentForDisplay } from '../../utils/formattingUtils';
import ScrollToTop from '../common/ScrollToTop';

const TemplatesTab = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exports, setExports] = useState([]);
  const [selectedExport, setSelectedExport] = useState(null);
  const [useTemplateDialogOpen, setUseTemplateDialogOpen] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [dataSourceType, setDataSourceType] = useState('clinical-intake');
  const [generating, setGenerating] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [clinicalIntakePreview, setClinicalIntakePreview] = useState(null);
  const [loadingIntakePreview, setLoadingIntakePreview] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);
  const fileInputRef = useRef(null);
  const generateInFlightRef = useRef(false);

    const loadExports = async () => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await documentExportService.getProtocolExports(projectId);
        const allExports = res?.data || res?.exports || [];
        // Prefer Design Studio exports if any; otherwise show all for debug/compatibility
        const dsExports = allExports.filter(
        (e) => e.metadata?.source === 'design-studio' || e.metadata?.source === 'template-generated'
        );
        const finalExports = dsExports.length > 0 ? dsExports : allExports;
        setExports(finalExports);
        if (finalExports.length > 0) {
          setSelectedExport(finalExports[0]);
        }
      } catch (err) {
        console.error('Error loading Design Studio templates:', err);
        setError('Failed to load templates.');
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadExports();
  }, [projectId]);

  const handleSelectExport = (exp) => {
    setSelectedExport(exp);
  };

  const handleUseTemplate = async () => {
    if (!selectedExport) return;
    
    setUseTemplateDialogOpen(true);
    setGenerationError(null);
    setGeneratedDocument(null);
    setShowFullPrompt(false);
    setClinicalIntakePreview(null);
    setUploadedDocuments([]);
    
    try {
      // Fetch template prompt and structure
      const response = await documentExportService.getTemplatePrompt(selectedExport._id);
      if (response.success) {
        setTemplateInfo(response.data);
      } else {
        setGenerationError('Failed to load template information');
      }
    } catch (err) {
      console.error('Error fetching template info:', err);
      setGenerationError('Failed to load template information');
    }
  };

  const handleLoadClinicalIntakePreview = async () => {
    if (!projectId || dataSourceType !== 'clinical-intake') return;
    
    setLoadingIntakePreview(true);
    try {
      const intakeData = await clinicalIntakeService.getLatestClinicalIntake(projectId);
      if (intakeData) {
        // Extract key fields for preview
        const preview = {
          studyTitle: intakeData.study_identification?.study_title || 'N/A',
          protocolNumber: intakeData.study_identification?.protocol_number || 'N/A',
          sponsor: intakeData.study_identification?.sponsor_name || 'N/A',
          hasStudyOverview: !!intakeData.study_overview,
          hasEndpoints: !!intakeData.endpoints_objectives,
          hasPopulation: !!intakeData.target_population,
          hasTreatments: !!intakeData.study_treatments,
          totalSections: Object.keys(intakeData).filter(key => 
            key !== '_id' && key !== '__v' && key !== 'createdAt' && key !== 'updatedAt'
          ).length
        };
        setClinicalIntakePreview(preview);
      } else {
        setClinicalIntakePreview({ error: 'No clinical intake form found for this project' });
      }
    } catch (err) {
      console.error('Error loading clinical intake preview:', err);
      setClinicalIntakePreview({ error: 'Failed to load clinical intake data' });
    } finally {
      setLoadingIntakePreview(false);
    }
  };

  // Load clinical intake preview when data source is selected
  // (Deduped) Load clinical intake preview when data source is selected.
  // Note: React StrictMode can run effects twice in dev; this guard prevents double fetch.
  useEffect(() => {
    if (
      useTemplateDialogOpen &&
      dataSourceType === 'clinical-intake' &&
      projectId &&
      !clinicalIntakePreview &&
      !loadingIntakePreview
    ) {
      handleLoadClinicalIntakePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useTemplateDialogOpen, dataSourceType, projectId]);

  const handleGenerateFromTemplate = async () => {
    if (!selectedExport || !templateInfo) return;
    if (generateInFlightRef.current || generating) return;
    
    // Validate data source
    if (dataSourceType === 'upload-document' && uploadedDocuments.length === 0) {
      setGenerationError('Please upload at least one document first');
      return;
    }
    
    generateInFlightRef.current = true;
    setGenerating(true);
    setGenerationError(null);
    
    try {
      // Combine all uploaded documents' text (without file names to avoid confusion)
      let combinedText = '';
      if (dataSourceType === 'upload-document' && uploadedDocuments.length > 0) {
        combinedText = uploadedDocuments.map((doc) => {
          return doc.extractedText;
        }).join('\n\n');
      }

      const dataSource = {
        type: dataSourceType,
        sourceId: dataSourceType === 'clinical-intake' ? projectId : null,
        data: dataSourceType === 'upload-document' && combinedText ? {
          extractedText: combinedText,
          fileNames: uploadedDocuments.map(doc => doc.fileName),
          documentCount: uploadedDocuments.length,
        } : null
      };
      
      const options = {
        regenerateAll: true,
        preserveStructure: true
      };
      
      const response = await documentExportService.generateFromTemplate(
        selectedExport._id,
        dataSource,
        options,
        projectId
      );
      
      if (response.success) {
        setGeneratedDocument(response.data);
      } else {
        setGenerationError(response.message || 'Failed to generate document');
      }
    } catch (err) {
      console.error('Error generating from template:', err);
      setGenerationError(err.response?.data?.message || err.message || 'Failed to generate document');
    } finally {
      setGenerating(false);
      generateInFlightRef.current = false;
    }
  };

  const handleDocumentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setUploadingDocument(true);
    setGenerationError(null);

    try {
      // Upload and extract text from each file (with blob storage)
      const newDocuments = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('document', file);
        if (projectId) {
          formData.append('projectId', projectId);
        }

        const response = await documentExportService.extractDocumentText(formData);
        
        if (response.success) {
          newDocuments.push({
            id: Date.now() + Math.random(), // Temporary ID
            file: file,
            extractedText: response.data.text,
            fileName: response.data.fileName,
            fileSize: response.data.fileSize,
            numPages: response.data.numPages || null,
            azureBlobName: response.data.azureBlobName,
            azureBlobUrl: response.data.azureBlobUrl,
          });
        } else {
          setGenerationError(response.message || `Failed to extract text from ${file.name}`);
        }
      }

      if (newDocuments.length > 0) {
        setUploadedDocuments(prev => [...prev, ...newDocuments]);
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setGenerationError(err.response?.data?.message || err.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleRemoveDocument = (documentId) => {
    setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  const handleSaveDocument = async () => {
    if (!generatedDocument || !projectId || !templateInfo) return;

    setSavingDocument(true);
    setGenerationError(null);

    try {
      // Prepare sections data in the format expected by ProtocolExport
      const sectionsData = generatedDocument.sections.map(section => ({
        sectionNumber: section.sectionNumber || '',
        title: section.title,
        output: section.content || section.output || '',
        content: section.content || section.output || '',
        subsections: []
      }));

      // Prepare metadata following Design Studio pattern
      const metadata = {
        source: 'template-generated',
        documentType: generatedDocument.documentType || 'protocol',
        generatedPrompt: templateInfo.prompt || null,
        templateId: templateInfo.templateId || null,
        totalSections: sectionsData.length,
      };

      // Generate filename based on template name and document type
      const documentType = generatedDocument.documentType || 'protocol';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `${documentType}-from-template-${timestamp}.docx`;

      // Save as ProtocolExport (like Design Studio does)
      await documentExportService.saveProtocolExport(
        projectId,
        null, // project ObjectId - optional
        'DOCX',
        filename,
        sectionsData,
        metadata,
        null, // documentBase64 - optional, can be added later if needed
        null  // fileSize - optional
      );

      // Success - close dialog and refresh templates list
      handleCloseDialog();
      
      // Refresh the exports list to show the new document
      loadExports();
      
    } catch (err) {
      console.error('Error saving document:', err);
      setGenerationError(err.message || 'Failed to save document');
    } finally {
      setSavingDocument(false);
    }
  };

  const handleCloseDialog = () => {
    setUseTemplateDialogOpen(false);
    setTemplateInfo(null);
    setGeneratedDocument(null);
    setGenerationError(null);
    setDataSourceType('clinical-intake');
    setShowFullPrompt(false);
    setClinicalIntakePreview(null);
    setUploadedDocuments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Load clinical intake preview when data source is selected
  // (moved earlier; keep only one effect to avoid duplicate fetches)

  // Helper: if the first meaningful block (p/h1–h6/div/text) is immediately
  // repeated (same text in one of the next few blocks), drop only the first copy.
  // This removes duplicated section headings at the very top but leaves all
  // later headings and content unchanged.
  const stripLeadingDuplicateBlock = (html) => {
    if (!html || typeof window === 'undefined') return html;
    try {
      const tempDiv = window.document.createElement('div');
      tempDiv.innerHTML = html;

      const blocks = Array.from(tempDiv.childNodes).filter(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE ||
          (node.nodeType === Node.TEXT_NODE && node.textContent.trim())
      );

      if (blocks.length < 2) return html;

      const normalize = (s) => s.replace(/\s+/g, ' ').trim().toLowerCase();
      const firstText = normalize(blocks[0].textContent || '');

      for (let i = 1; i < Math.min(blocks.length, 5); i += 1) {
        const t = normalize(blocks[i].textContent || '');
        if (t && t === firstText) {
          tempDiv.removeChild(blocks[0]);
          return tempDiv.innerHTML;
        }
      }

      return html;
    } catch {
      return html;
    }
  };

  const sectionRefs = useRef({});

  const renderContent = () => {
    if (!selectedExport) {
      return (
        <Typography variant="body2" color="text.secondary">
          Select a template on the left to view its content.
        </Typography>
      );
    }

    const sections = selectedExport.sections || [];
    const isICF = selectedExport.metadata?.documentType === 'icf';

    return (
      <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
        {/* Section Navigation Chips - Matching Design Studio style */}
        {sections.length > 0 && (
          <Paper
        sx={{
          p: 2,
              mb: 3,
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {sections
                .sort((a, b) => {
                  const aMatch = (a.sectionNumber || a.title || '').match(/^(\d+)/);
                  const bMatch = (b.sectionNumber || b.title || '').match(/^(\d+)/);
                  if (aMatch && bMatch) {
                    return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
                  }
                  return (a.title || '').localeCompare(b.title || '');
                })
                .map((section, idx) => (
                  <Chip
                    key={section.sectionId || idx}
                    label={section.title || `Section ${idx + 1}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const element = sectionRefs.current[section.sectionId || idx];
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  />
                ))}
            </Box>
          </Paper>
        )}

        {/* Section Content - Matching Design Studio style */}
        <Box>
          {sections.map((section, idx) => {
            const sectionId = section.sectionId || idx;
            const rawContent = section.content || section.output || '';
            
            // Process content for display (handles markdown, HTML, etc.)
            const processedContent = processContentForDisplay(rawContent);
            
            // Remove duplicate leading blocks
            const cleanedContent = stripLeadingDuplicateBlock(processedContent);

            return (
              <Paper
                key={sectionId}
                id={`section-${sectionId}`}
                ref={(el) => {
                  if (el) sectionRefs.current[sectionId] = el;
                }}
                sx={{
                  mb: 2,
                  p: 3,
          borderRadius: 2,
                  bgcolor: '#f8f9fa',
          border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5, 
                    fontWeight: 600, 
                    color: 'primary.main' 
                  }}
                >
                  {section.sectionNumber ? `${section.sectionNumber}. ` : ''}
                  {section.title || `Section ${idx + 1}`}
                </Typography>
                <Box
                  component="div"
                  sx={{
                    fontFamily: isICF
            ? '"Arial", "Calibri", "Verdana", sans-serif'
            : '"Times New Roman", serif',
          fontSize: '12pt',
          lineHeight: 1.6,
                    color: '#1a1a1a',
          '& p': {
                      mb: 1.5, 
                      lineHeight: 1.6,
            textAlign: 'justify',
          },
                    '& h1, & h2, & h3, & h4, & h5, & h6': { 
                      mt: 2, 
                      mb: 1, 
                      fontWeight: 700, 
                      fontSize: '1em',
                      fontFamily: isICF
                        ? '"Arial", "Calibri", "Verdana", sans-serif'
                        : '"Times New Roman", serif',
                    },
                    '& ul, & ol': { 
                      pl: 3, 
                      mb: 2,
                      lineHeight: 1.6,
                    },
                    '& li': { 
                      mb: 0.8,
                      lineHeight: 1.6,
                    },
                    '& strong, & b': {
            fontWeight: 700,
          },
                    '& em, & i': {
                      fontStyle: 'italic',
                    },
        }}
        dangerouslySetInnerHTML={{
                    __html: cleanedContent
                      ? sanitizeRichHtml(cleanedContent)
                      : '<p style="color: #999; font-style: italic;">No content available</p>',
        }}
      />
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: '70vh', height: '100%' }}>
      <Paper
        sx={{
          width: 320,
          flexShrink: 0,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Design Studio Templates
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && exports.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No templates yet. Export a document from Design Studio to see it here.
          </Typography>
        )}
        {exports.length > 0 && (
          <>
            <List dense sx={{ mt: 1, overflow: 'auto', flex: 1 }}>
            {exports.map((exp) => (
              <ListItemButton
                key={exp._id}
                selected={selectedExport && selectedExport._id === exp._id}
                onClick={() => handleSelectExport(exp)}
              >
                <ListItemText
                  primary={exp.filename || 'Design Studio Export'}
                  secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {exp.metadata?.documentType
                      ? exp.metadata.documentType.toUpperCase()
                            : 'Protocol'}
                        </Typography>
                        {(exp.metadata?.generatedPrompt || exp.metadata?.designStudioSessionId) ? (
                          <Chip
                            label="Reusable"
                            size="small"
                            color="success"
                            sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                          />
                        ) : (
                          <Chip
                            label="Not reusable"
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, height: 18, fontSize: '0.65rem', color: 'text.secondary' }}
                          />
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            ))}
          </List>
            {selectedExport && (() => {
              const canUse = Boolean(
                selectedExport.metadata?.generatedPrompt || selectedExport.metadata?.designStudioSessionId
              );
              const button = (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleUseTemplate}
                  disabled={!canUse}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Use Template
                </Button>
              );
              return canUse ? button : (
                <Tooltip title="This export has no saved Design Studio prompt/session to reuse, so it can't be used as a template.">
                  <span style={{ display: 'block' }}>{button}</span>
                </Tooltip>
              );
            })()}
          </>
        )}
      </Paper>
      <Paper
        sx={{
          flex: 1,
          minWidth: 0,
          p: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderContent()}
      </Paper>

      {/* Scroll to Top Button */}
      <ScrollToTop />

      {/* Use Template Dialog */}
      <Dialog
        open={useTemplateDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Generate Document from Template
        </DialogTitle>
        <DialogContent>
          {templateInfo && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Template
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {templateInfo.templateName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {templateInfo.totalSections} sections • {templateInfo.documentType}
                </Typography>
              </Box>

              <Divider />

              {/* Template Structure */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Template Structure ({templateInfo.structure?.length || 0} sections)
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    maxHeight: 150,
                    overflow: 'auto',
                    bgcolor: 'grey.50',
                  }}
                >
                  {templateInfo.structure?.map((section, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        display: 'block',
                        py: 0.5,
                        fontFamily: 'monospace',
                      }}
                    >
                      {section.sectionNumber ? `${section.sectionNumber}. ` : ''}
                      {section.title}
                    </Typography>
                  ))}
                </Paper>
              </Box>

              <Divider />

              {/* Data Source Selection */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Data Source
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Select Data Source</InputLabel>
                  <Select
                    value={dataSourceType}
                    onChange={(e) => {
                      setDataSourceType(e.target.value);
                      setClinicalIntakePreview(null);
                      setUploadedDocuments([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    label="Select Data Source"
                  >
                    <MenuItem value="clinical-intake">
                      Clinical Intake Form (Current Project)
                    </MenuItem>
                    <MenuItem value="upload-document">
                      Upload Document
                    </MenuItem>
                    <MenuItem value="manual" disabled>
                      Manual Input (Coming Soon)
                    </MenuItem>
                  </Select>
                </FormControl>
                
                {/* Clinical Intake Preview */}
                {dataSourceType === 'clinical-intake' && (
                  <Box sx={{ mt: 2 }}>
                    {loadingIntakePreview ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Loading clinical intake data...
                        </Typography>
                      </Box>
                    ) : clinicalIntakePreview?.error ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {clinicalIntakePreview.error}
                      </Alert>
                    ) : clinicalIntakePreview ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mt: 1,
                          bgcolor: 'info.light',
                          bgcolor: 'rgba(33, 150, 243, 0.08)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <InfoIcon fontSize="small" color="info" />
                          <Typography variant="caption" fontWeight="medium">
                            Clinical Intake Data Available
                          </Typography>
                        </Box>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" display="block">
                            <strong>Study Title:</strong> {clinicalIntakePreview.studyTitle}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>Protocol Number:</strong> {clinicalIntakePreview.protocolNumber}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>Sponsor:</strong> {clinicalIntakePreview.sponsor}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            <strong>Data Sections:</strong> {clinicalIntakePreview.totalSections} sections available
                            {clinicalIntakePreview.hasStudyOverview && ' • Study Overview'}
                            {clinicalIntakePreview.hasEndpoints && ' • Endpoints'}
                            {clinicalIntakePreview.hasPopulation && ' • Population'}
                            {clinicalIntakePreview.hasTreatments && ' • Treatments'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          This data will be merged with the template prompt to generate new document content.
                        </Typography>
                      </Paper>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleLoadClinicalIntakePreview}
                        sx={{ mt: 1 }}
                      >
                        Preview Clinical Intake Data
                      </Button>
                    )}
                  </Box>
                )}

                {/* Document Upload */}
                {dataSourceType === 'upload-document' && (
                  <Box sx={{ mt: 2 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.json"
                      multiple
                      onChange={handleDocumentUpload}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={uploadingDocument ? <CircularProgress size={20} /> : <UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingDocument}
                      sx={{ mb: 2 }}
                    >
                      {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                    </Button>

                    {uploadedDocuments.length > 0 ? (
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <List>
                          {uploadedDocuments.map((doc) => (
                            <ListItem
                              key={doc.id}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                py: 1.5,
                              }}
                            >
                              <DescriptionIcon sx={{ mr: 1.5, fontSize: 20, color: 'primary.main' }} />
                              <ListItemText
                                primary={doc.fileName}
                                secondary={
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      {(doc.fileSize / 1024).toFixed(1)} KB
                                    </Typography>
                                    {doc.numPages && (
                                      <Typography variant="caption" display="block">
                                        {doc.numPages} page{doc.numPages !== 1 ? 's' : ''}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      {doc.extractedText.length.toLocaleString()} characters extracted
                                    </Typography>
                                  </Box>
                                }
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleRemoveDocument(doc.id)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          {uploadedDocuments.length} document{uploadedDocuments.length !== 1 ? 's' : ''} ready. Text from all documents will be combined and merged with the template prompt.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          No documents uploaded yet
                          <br />
                          <Typography variant="caption" component="span">
                            Upload PDF, DOCX, DOC, TXT, or JSON files to use as reference
                          </Typography>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Template Prompt */}
              {templateInfo.hasPrompt && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Template Prompt
                    </Typography>
                    <Button
                      size="small"
                      endIcon={showFullPrompt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => setShowFullPrompt(!showFullPrompt)}
                    >
                      {showFullPrompt ? 'Show Less' : 'Show Full Prompt'}
                    </Button>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      maxHeight: showFullPrompt ? 400 : 200,
                      overflow: 'auto',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.75rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {showFullPrompt
                        ? templateInfo.prompt
                        : templateInfo.prompt?.substring(0, 500) + (templateInfo.prompt?.length > 500 ? '...' : '')}
                    </Typography>
                    {!showFullPrompt && templateInfo.prompt?.length > 500 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}
                      >
                        ({templateInfo.prompt.length} characters total - click "Show Full Prompt" to see all)
                      </Typography>
                    )}
                  </Paper>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    This prompt will be combined with the selected data source to generate the new document.
                  </Typography>
                </Box>
              )}

              {generationError && (
                <Alert severity="error">{generationError}</Alert>
              )}

              {generatedDocument && (
                <Box>
                  <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                    Document generated successfully! {generatedDocument.totalSections} sections created.
                  </Alert>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Generated Sections:
                    </Typography>
                    {generatedDocument.sections.map((section, idx) => (
                      <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {section.sectionNumber ? `${section.sectionNumber}. ` : ''}
                          {section.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {section.content?.substring(0, 100)}
                          {section.content?.length > 100 ? '...' : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
      </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {generatedDocument ? 'Close' : 'Cancel'}
          </Button>
          {generatedDocument && (
            <Button
              variant="contained"
              onClick={handleSaveDocument}
              disabled={savingDocument || !projectId}
              startIcon={savingDocument ? <CircularProgress size={16} /> : <SaveIcon />}
              color="success"
            >
              {savingDocument ? 'Saving...' : 'Save Document'}
            </Button>
          )}
          {!generatedDocument && (
            <Button
              variant="contained"
              onClick={handleGenerateFromTemplate}
              disabled={
                generating || 
                !templateInfo?.hasPrompt || 
                (dataSourceType === 'upload-document' && uploadedDocuments.length === 0)
              }
              startIcon={generating ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            >
              {generating ? 'Generating...' : 'Generate Document'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesTab;


